export type Nullable<T> = T | null

export type QRType = 'C2B' | 'B2B'
export type OFDBillMode = 'MANUAL' | 'AUTO'
export type OFDPaymentMethod =
  | 'FULL_PREPAYMENT'
  | 'PART_PREPAYMENT'
  | 'ADVANCE'
  | 'FULL_PAYMENT'
  | 'PART_PAYMENT_AND_CREDIT'
  | 'CREDIT'
  | 'LOAN_PAYMENT'

export type OFDPaymentSubject =
  | 'GOOD'
  | 'JOB'
  | 'SERVICE'
  | 'RESULT_OF_INTELLECTUAL_ACTIVITY'
  | 'PAYMENT'
  | 'AGENTS_COMMISSION'
  | 'EXCISABLE_GOOD'
  | 'BET'
  | 'BET_WIN'
  | 'LOTTERY_TICKET'
  | 'LOTTERY_WIN'
  | 'COMPLEX_PAYMENT_TARGET'
  | 'PROPERTY_LAW'
  | 'NON_OPERATION_INCOME'
  | 'OTHER_PAYMENT'
  | 'TRADE_FEE'
  | 'RESORT_FEE'
  | 'PLEDGE'
  | 'CONSUMPTION'
  | 'PENSION_INSURANCE_ENTREPRENEUR'
  | 'PENSION_INSURANCE'
  | 'MEDICAL_INSURANCE_ENTREPRENEUR'
  | 'MEDICAL_INSURANCE'
  | 'SOCIAL_INSURANCE'
  | 'CASINO_PAYMENT'
  | 'ANOTHER_PAYMENT_TARGET'

export type SbpStatus = 'NEW' | 'PAID' | 'REVERSED' | 'REJECTED'
export type SbpType = 'INCOME' | 'REVERSE' | 'INCOME_B2B' | 'B2B' | 'C2B'
export type SbpPaymentMethod = 'B2B' | 'C2B' | 'B2C'
export type QrC2BType = '01' | '02'
export enum B2CTypeTable {
  PAYMENTS = 'PAYMENTS',
  REGISTRIES = 'REGISTRIES',
}

export type OptionsValue<T> = { value: T; label: string }[]

export type B2CTypePayment = 'ONE_TIME' | 'FROM_REGISTRY' | 'ALL'

export type AcqStatus =
  | 'ACCEPTED'
  | 'REGISTERED'
  | 'NOT_PAID'
  | 'PAID'
  | 'REVERSED'
  | 'CANCELED'
  | 'HOLD'
  | 'PARTIAL_REVERSED'
export type AcqType = 'INCOME_ACCOUNT' | 'PAY_BASKET'

export type RouteHandle<Params extends Record<string, any>> = {
  title?: string | ((params: Params) => string)
  pathTitle?: string | ((params: Params) => string)
  goBack?: boolean
  permissionKey?: keyof UserPermissions | (keyof UserPermissions)[]
}

export interface TableData<T> {
  totalElements: number
  totalPages: number
  number: number
  size: number
  content: Array<T>
  sort: string | null
  first: boolean
  last: boolean
  numberOfElements: number
}

export interface SbpPayment {
  operationId: number
  tspName: string | null
  errorId: number | null
  errorMessage: string | null
  reference: string | null
  qrcId?: string
  qrcType?: QrC2BType
  extEntityId?: string
  merchantId?: string
  rcvTspAddress: string | null
  type: SbpType
  state: SbpStatus
  amount?: number
  accountNumber: string
  paymentPurpose: string | null
  subscriptionPurpose: string | null
  mediaType?: string
  size: number
  payload: string
  image: string | null
  sndPam: string | null
  sndPhone: string | null
  sndLegalEntityName: string | null
  sndInn: string | null
  sndAccountNum: string | null
  sndBic: string | null
  sndBankNameRus: string | null
  trxId: string
  cur: string | null
  trxTime: number
  redirectUrl: string | null
  requestId: number | null
  expDt: number
  expireQr: number
  commissionMerchant: number
  toMerchant: number
  partnerIp: string
  // unix timestamp ms
  createTime: number
  // unix timestamp ms
  updateTime: number
  refundedAmount: number
  reversedOperationId: number | null
  availableForReverse: number | null
  canBeReversed: boolean
  showReverseList: boolean
}

export interface OperationRequest {
  id: number
  operationId: number
  partnerId: number
  clientName: string
  clientEmail: string
  orderNumber: string
  orderSum: number
  url: string
  // dd.mm.yyyy
  createTime: string
  // dd.mm.yyyy
  validTo: string
}

export interface AcqPayment {
  accountNumber: Nullable<string>
  additionalIdentifier: Nullable<string>
  address: Nullable<string>
  agent: Nullable<string>
  agreementNumber: Nullable<string>
  amount: Nullable<string>
  amountIncomeClient: Nullable<string>
  amountIncomeMerchant: Nullable<string>
  amountRur: Nullable<string>
  amountReturn: Nullable<string>
  amountUnits: Nullable<string>
  bankIssuer: Nullable<string>
  bankIssuerCountry: Nullable<string>
  cardType: Nullable<string> // mb ne nullable
  clientEmail: Nullable<string> // mb ne nullable
  clientFio: Nullable<string>
  comissionPayer: Nullable<string>
  comissionReceiver: Nullable<string>
  commissionAgent: Nullable<string>
  commissionITO: Nullable<string>
  contractNumber: Nullable<string>
  creditContractMerchant: Nullable<string>
  currency: Nullable<string>
  currencySymbol: Nullable<string>
  // dd.mm.yyyy hh:mm:ss
  date: Nullable<string>
  id: number
  merchant: Nullable<string>
  name: Nullable<string>
  pan: Nullable<string>
  partnerId: Nullable<number>
  payFor: Nullable<string>
  paymentTemplateId: Nullable<number>
  phone: Nullable<string>
  reasonCode: Nullable<string>
  reasonMessage: Nullable<string>
  reference: Nullable<string>
  refundDocumentLink: Nullable<string>
  registryDate: Nullable<string>
  registryNumber: Nullable<string>
  reverseAllow: boolean
  reverseAsync: boolean
  // какие стейты могут быть
  state: AcqStatus
  templateInfo: Nullable<string>
  // какие тайпы могут быть
  type: AcqType
  writeOffAmount: Nullable<string>
  writeOffPercent: Nullable<string>
}

export interface TemplatePayment {
  id: number
  partnerId: number
  paymentInfoShort: Nullable<string>
  paymentInfoFull: Nullable<string>
  amount: Nullable<number>
  paymentCountLimit: number
  createTime: string
  validTo: string
  productCode: Nullable<string>
  url: string
  unit: Nullable<string>
  // Необходимость ввода адреса доставки при создании платежа
  addressNeeded: boolean
  // Необходимость ввода номера договора при создании платежа
  agreementNeeded: boolean
  // Необходимость ввода количества при создании платежа
  amountUnitsNeeded: boolean
  // Доступность ввода любой суммы при создании платежа
  freeSumAvailable: boolean

  ofdPaymentMethodType?: OFDPaymentMethod
  ofdPaymentSubjectType?: OFDPaymentSubject
  name: Nullable<string>
  nomenclatureCode: Nullable<string>
}

export interface SbpRefundOperation {
  operationId: number
  tspId: number
  tspName: null | string
  errorId: number
  errorMessage: string | null
  reference: string | null
  qrcId: string
  extEntityId: string
  merchantId: string
  type: 'REVERSE'
  state: SbpStatus
  amount: number
  accountNumber: string
  paymentPurpose: string | null
  subscriptionPurpose: string | null
  qrcType: string | null
  mediaType: string | null
  size: number | null
  payload: string | null
  image: string | null
  sndPam: string | null
  sndPhone: string | null
  trxId: string | null
  cur: string | null
  trxTime: number | null
  redirectUrl: string | null
  expDt: number | null
  expireQr: string | null
  commissionMerchant: number
  toMerchant: number
  partnerIp: string
  createTime: number
  updateTime: number
  refundedAmount: number
  reversedOperationId: number
  availableForReverse: number | null
  showReverseList: boolean
  canBeReversed: boolean
}

export interface B2BQr {
  requestId: number
  reference: Nullable<number>
  qrcId: Nullable<string>
  isQrActive?: boolean
  qrcType: string
  extEntityId: string
  accountNumber: Nullable<string>
  amount: Nullable<number>
  commissionMerchant: Nullable<number>
  toMerchant: Nullable<number>
  // unix timestamp ms
  createTime: number
  // unix timestamp ms
  updateTime: number
  cur: string | null
  expDt: number
  // unix timestamp ms
  expireQr: number
  trxTime: Nullable<number>
  mediaType: Nullable<string>
  merchantId: string
  payload: string
  size: number
  paymentPurpose: Nullable<string>
  rcvTspAddress: Nullable<string>
  redirectUrl: Nullable<string>
  state: 'REGISTERED' | 'PAID'
  subscriptionPurpose: Nullable<string>
  type: QRType
  image: null
  reusable: boolean
  takeTax: false
  totalTaxAmount: Nullable<number>
  uip: Nullable<string>
}

export interface C2BQr extends Omit<B2BQr, 'state'> {
  state: 'REGISTERED' | 'REJECTED'
}

export interface RegisterB2BQr {
  merchantId: string
  reference?: string
  amount?: number
  purpose: string
  reusable: boolean
  takeTax: boolean
  totalTaxAmount?: number
  uip?: string
}

export interface ErrorResponse {
  errorType?: 'NOT_AUTHORIZED' | 'NOT_FOUND'
  errorMessage: string
  errorDetails: null
}

export interface TSP {
  merchantId: string
  name: string
  state: 'ACTIVE'
  tspId: number
}

export interface User {
  id: number
  login: string
  phone: string
  firstName: string
  lastName: string
  middleName: string
  partner: {
    id: 1
    name: string
    accountNumber: string
  }
  accessType: 'FULL' | string
  tspList: Array<TSP>
  permissions: UserPermissions
}

export interface UserPermissions {
  showSbp: boolean
  showOneTimePage: boolean
  showStaticTemplate: boolean
  showAcqOperations: boolean
  showQrC2b: boolean
  showQrB2b: boolean
  showB2c: boolean
  ofd: boolean
  showQrC2bStatic: boolean
}

export interface OfdPosition {
  quantity: number
  price: string
  tax: number
  text: string
  paymentMethodType: number
  paymentSubjectType: number
  nomenclatureCode?: string
  unitOfMeasurement?: string
}

export interface OfdParams {
  email?: string
  positions?: Array<OfdPosition>
}
export interface RefundOperationParams extends OfdParams {
  reverseSum: string
  purpose?: string
  emailCode: string
}

export interface RegisterC2BQrParams extends OfdParams {
  merchantId: string
  reference?: string
  amount: number
  purpose: string
  expireMin?: number
  qrcType?: QrC2BType
}
export interface RegisterStaticC2BQrParams {
  merchantId: string
  qrcType: QrC2BType
  reference?: string
  amount?: number
  purpose?: string
}

export interface SendUrlParams {
  clientPhone?: string
  clientEmail?: string
}

export interface OFDFields {
  ofdBillMode: OFDBillMode
  ofdPaymentMethodType?: OFDPaymentMethod
  ofdPaymentSubjectType?: OFDPaymentSubject
  ofdName?: string
  ofdNomenclatureCode?: string
}
export interface CreateOperationRequestParams extends OFDFields {
  clientName: string
  clientEmail: string
  orderNumber: string
  orderSum: string | number
  productCode: string
}

export interface CreateTemplateRequestParams extends OFDFields {
  paymentInfoShort: string
  paymentInfoFull: string
  amount: string | number
  paymentCountLimit: string | number
  unit: string
  validDays: string
  validHours: string
  validMinutes: string
  productCode: string

  addressNeeded: boolean | 'true' | 'false'
  agreementNeeded: boolean | 'true' | 'false'
  amountUnitsNeeded: boolean | 'true' | 'false'
  freeSumAvailable: boolean | 'true' | 'false'
}

export interface AutoReportParams {
  emails: string[]
  daily: boolean
  monthly: boolean
  formatReport: 'xlsx' | 'csv'
  kind: 'ACQ' | 'SBP' | 'B2C_SBP'
}

export interface AutoReportDeleteBodyDto {
  items: { id: number; emails: string[] }[]
}

export interface AutoReportGetParams {
  report_kind: 'ACQ' | 'SBP' | 'B2C_SBP'
}

export interface AutoReportSubscriptionDtoResponse {
  id: number
  email: string
  reportKind: 'ACQ' | 'SBP' | 'B2C_SBP'
  frequency: 'DAILY' | 'MONTHLY'
  reportType: 'XLSX' | 'CSV'
}

export interface SbpReportParams {
  dateFrom: string
  dateTo: string
  operationState?: string
  sbpRequest?: string
  operationType?: QRType
  formatReport: ReportFormat
}

export type ReportFormat = 'xlsx' | 'csv'

export interface ReportParams {
  dateFrom: string
  dateTo: string
  formatReport: ReportFormat
}

export interface ApiErrorResponse {
  errorType: string | 'NOT_AUTHORIZED' | 'INCORRECT_REQUEST'
  errorMessage: string
  errorDetails: Nullable<string>
  message?: string
}

export interface PersonalSettingsDto {
  pages: Record<string, Record<string, string[]>>
}
